import { use } from "react";
import { BikeContext } from "../context/bikeContext";
import {
  CountingFrom,
  ForkSettings as FS,
  PostSettingsBody,
} from "../http/onlyclicksApi";
import { translate } from "../i18n";
import { Language } from "../types";
import styles from "./ForkSettings.module.css";
import { UseFormReturn } from "./Form";
import Panel from "./Panel";
import Setting from "./Setting";

interface Props {
  fork?: Omit<FS, "id" | "created_at">;
  isEditMode: boolean;
  form: UseFormReturn<PostSettingsBody>;
  countingFrom: CountingFrom;
}

const ForkSettings = ({ fork, isEditMode, form, countingFrom }: Props) => {
  const { bike } = use(BikeContext);
  const i18n = translate(Language.English);
  const mode = isEditMode ? "edit" : "view";

  if (!bike) return null;

  const clicksMinValue = () => {
    switch (countingFrom) {
      case "fully_closed":
        return -40;
      case "fully_open":
        return 0;
    }
  };

  const clicksMaxValue = () => {
    switch (countingFrom) {
      case "fully_closed":
        return 0;
      case "fully_open":
        return 40;
    }
  };

  const validateClicks = (value: number | undefined) => {
    if (value && (value < clicksMinValue() || value > clicksMaxValue())) {
      return `value must be between ${clicksMinValue()} and ${clicksMaxValue()}`;
    }
    return undefined;
  };

  return (
    <Panel title={i18n.fork}>
      {bike.fork ? (
        <ol className={styles.foo}>
          {bike.fork.show_sag && (
            <li>
              <form.Field
                name="fork.sag"
                validators={{
                  onBlur: ({ value }) => {
                    if (value && (value < 0 || value > 100)) {
                      return "value must be between 0 and 100";
                    }
                    return undefined;
                  },
                }}
              >
                {(field) => (
                  <Setting
                    key={field.name}
                    label={i18n.sag}
                    value={{ curr: field.state.value, prev: fork?.sag?.curr }}
                    unit="%"
                    minValue={0}
                    maxValue={100}
                    mode={mode}
                    onChange={(sag) => field.handleChange(sag)}
                    countingFrom={countingFrom}
                  />
                )}
              </form.Field>
            </li>
          )}
          {bike.fork.type === "air" && (
            <li>
              <form.Field
                name="fork.psi"
                validators={{
                  onBlur: ({ value }) => {
                    if (value && (value < 0 || value > 350)) {
                      return "value must be between 0 and 350";
                    }
                    return undefined;
                  },
                }}
              >
                {(field) => (
                  <Setting
                    key={field.name}
                    label={i18n.air_pressure}
                    value={{ curr: field.state.value, prev: fork?.psi?.curr }}
                    unit={i18n.psi}
                    minValue={0}
                    maxValue={350}
                    mode={mode}
                    onChange={(psi) => field.handleChange(psi)}
                    countingFrom={countingFrom}
                  />
                )}
              </form.Field>
            </li>
          )}
          {bike.fork.type === "coil" && (
            <li>
              <form.Field
                name="fork.lbs"
                validators={{
                  onBlur: ({ value }) => {
                    if (value && (value < 200 || value > 800)) {
                      return "value must be between 200 and 800";
                    }
                    return undefined;
                  },
                }}
              >
                {(field) => (
                  <Setting
                    label={i18n.spring_weight}
                    value={{ curr: field.state.value, prev: fork?.lbs?.curr }}
                    unit={i18n.lbs}
                    minValue={200}
                    maxValue={800}
                    mode={mode}
                    onChange={(lbs) => field.handleChange(lbs)}
                    countingFrom={countingFrom}
                  />
                )}
              </form.Field>
            </li>
          )}
          {bike.fork.show_lsc && (
            <li>
              <form.Field
                name="fork.lsc"
                validators={{
                  onBlur: ({ value }) => validateClicks(value),
                }}
              >
                {(field) => (
                  <Setting
                    label={i18n.lsc(true)}
                    value={{ curr: field.state.value, prev: fork?.lsc?.curr }}
                    unit={i18n.clicks}
                    minValue={clicksMinValue()}
                    maxValue={clicksMaxValue()}
                    mode={mode}
                    onChange={(lsc) => field.handleChange(lsc)}
                    countingFrom={countingFrom}
                  />
                )}
              </form.Field>
            </li>
          )}
          {bike.fork.show_hsc && (
            <li>
              <form.Field
                name="fork.hsc"
                validators={{
                  onBlur: ({ value }) => validateClicks(value),
                }}
              >
                {(field) => (
                  <Setting
                    label={i18n.hsc(true)}
                    value={{ curr: field.state.value, prev: fork?.hsc?.curr }}
                    unit={i18n.clicks}
                    minValue={clicksMinValue()}
                    maxValue={clicksMaxValue()}
                    mode={mode}
                    onChange={(hsc) => field.handleChange(hsc)}
                    countingFrom={countingFrom}
                  />
                )}
              </form.Field>
            </li>
          )}
          {bike.fork.show_lsr && (
            <li>
              <form.Field
                name="fork.lsr"
                validators={{
                  onBlur: ({ value }) => validateClicks(value),
                }}
              >
                {(field) => (
                  <Setting
                    label={i18n.lsr(bike.fork!.show_hsr, true)}
                    value={{ curr: field.state.value, prev: fork?.lsr?.curr }}
                    unit={i18n.clicks}
                    minValue={clicksMinValue()}
                    maxValue={clicksMaxValue()}
                    mode={mode}
                    onChange={(lsr) => field.handleChange(lsr)}
                    countingFrom={countingFrom}
                  />
                )}
              </form.Field>
            </li>
          )}
          {bike.fork.show_hsr && (
            <li>
              <form.Field
                name="fork.hsr"
                validators={{
                  onBlur: ({ value }) => validateClicks(value),
                }}
              >
                {(field) => (
                  <Setting
                    label={i18n.hsr(true)}
                    value={{ curr: field.state.value, prev: fork?.hsr?.curr }}
                    unit={i18n.clicks}
                    minValue={clicksMinValue()}
                    maxValue={clicksMaxValue()}
                    mode={mode}
                    onChange={(hsr) => field.handleChange(hsr)}
                    countingFrom={countingFrom}
                  />
                )}
              </form.Field>
            </li>
          )}
          {bike.fork.show_volume_spacers && (
            <li>
              <form.Field
                name="fork.volume_spacers"
                validators={{
                  onBlur: ({ value }) => {
                    if (value && (value < 0 || value > 10)) {
                      return "value must be between 0 and 10";
                    }
                    return undefined;
                  },
                }}
              >
                {(field) => (
                  <Setting
                    label={i18n.volume_spacers}
                    value={{
                      curr: field.state.value,
                      prev: fork?.volume_spacers?.curr,
                    }}
                    unit={i18n.quantity}
                    minValue={0}
                    maxValue={10}
                    mode={mode}
                    onChange={(volumeSpacers) =>
                      field.handleChange(volumeSpacers)
                    }
                    countingFrom={countingFrom}
                  />
                )}
              </form.Field>
            </li>
          )}
          {bike.fork.show_negative_psi && (
            <li>
              <form.Field
                name="fork.negative_psi"
                validators={{
                  onBlur: ({ value }) => {
                    if (value && (value < 0 || value > 350)) {
                      return "value must be between 0 and 350";
                    }
                    return undefined;
                  },
                }}
              >
                {(field) => (
                  <Setting
                    key={field.name}
                    label={i18n.negative_air_pressure(true)}
                    value={{
                      curr: field.state.value,
                      prev: fork?.negative_psi?.curr,
                    }}
                    unit={i18n.psi}
                    minValue={0}
                    maxValue={350}
                    mode={mode}
                    onChange={(negativePsi) => field.handleChange(negativePsi)}
                    countingFrom={countingFrom}
                  />
                )}
              </form.Field>
            </li>
          )}
        </ol>
      ) : (
        <span>Fork not configured.</span>
      )}
    </Panel>
  );
};

export default ForkSettings;
